/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { TemplateResult } from "lit";
import type { WhPaginationElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-pagination";
import type { WhCarouselElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-carousel";
import { localized, msg } from "@lit/localize";
import { css, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";

import type { CustomEventDetail, ChangePageEvent } from "../../types/carousel";
import type { ServiceCard, ServicesContent } from "../../types/content";
import type { WebKioskServicesModalElement } from "../../elements/web-kiosk-services-modal";
import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-card";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-carousel";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-pagination";
import "../../elements/web-kiosk-services-modal";
import "../../elements/web-kiosk-home-link";
import { elementsIntoPages } from "../../utilities/pagination.utility";
import { WebKioskPageElement } from "../web-kiosk-page";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-services": WebKioskServicesElement;
  }
}

@localized()
@customElement("web-kiosk-services")
export class WebKioskServicesElement extends WebKioskPageElement {
  private readonly cardsPerPage = 6;

  @state() content: ServicesContent | undefined = undefined;

  @property() selectedCard: ServiceCard | undefined = undefined;

  @query("web-kiosk-services-modal") modal:
    | WebKioskServicesModalElement
    | undefined;

  @query("wh-pagination") paginationElement: WhPaginationElement | undefined;

  @query("wh-carousel") carousel: WhCarouselElement | undefined;

  @property({ type: Number }) carouselCurrentPageNumber = 1;

  @property({ type: Number }) carouselActiveNumber?: number;

  showInfo(card: ServiceCard): void {
    this.selectedCard = card;

    this.modal?.show();
  }

  changePage({ detail }: ChangePageEvent): void {
    this.carouselCurrentPageNumber = detail.newPagePosition;
    this.carouselActiveNumber = detail.newPagePosition - 1;
  }

  updatePagination({ detail: { activeIndex } }: CustomEventDetail): void {
    this.carouselCurrentPageNumber = activeIndex + 1;
  }

  renderCard(card: ServiceCard): TemplateResult {
    return html`<div class="card-container">
      <wh-card
        flat
        size="large"
        @click=${() => {
          this.showInfo(card);
        }}
      >
        <section class="card-inner">
          <img src=${card.icon} class="card-icon" alt=${msg("Details")} />
          <div class="card-texts">
            <h3>${card.title}</h3>
            <p>${card.availability}</p>
          </div>

          <img
            src="assets/services/arrow_icon.svg"
            class="arrow-icon"
            alt=${msg("Details")}
          />
        </section>
      </wh-card>
    </div>`;
  }

  renderPageCards(cards: ServiceCard[]): TemplateResult[] {
    return cards.map((card) => this.renderCard(card));
  }

  renderSlides(cards: ServiceCard[]): TemplateResult[] {
    const pages = elementsIntoPages(
      cards,
      this.cardsPerPage
    ) as ServiceCard[][];

    return pages.map(
      (page) =>
        html`<section wh-carousel-snap class="carousel-slide">
          ${this.renderPageCards(page)}
        </section>`
    );
  }

  renderCarousel(
    cards: ServiceCard[] | undefined
  ): TemplateResult | typeof nothing {
    if (!cards || cards.length === 0) {
      return nothing;
    }

    return html`<wh-carousel
      active=${ifDefined(this.carouselActiveNumber)}
      @scrollcomplete=${this.updatePagination}
    >
      ${this.renderSlides(cards)}
    </wh-carousel>`;
  }

  renderPagination(cardsCount: number): TemplateResult | typeof nothing {
    if (cardsCount <= this.cardsPerPage) {
      return nothing;
    }

    const totalPages = Math.ceil(cardsCount / this.cardsPerPage);

    return html`<div id="pagination-container">
      <wh-pagination
        variant="number-of"
        current-page-number=${this.carouselCurrentPageNumber}
        total-pages=${totalPages}
        @paginate=${this.changePage}
      >
        <span slot="prefix"
          >${msg("Page", { id: "home-carousel-prefix" })}</span
        >
        <span slot="separator"> / </span>
      </wh-pagination>
    </div>`;
  }

  render(): TemplateResult {
    return html`<section class="page-body">
      <div class="body-inner">
        <web-kiosk-home-link
          link=${this.createRelativeLink("home")}
        ></web-kiosk-home-link>

        <div class="body-content">
          <section id="about-text">
            <h1>${msg("Walgreens Health Corner Services")}</h1>
            <h2>${this.content?.heading}</h2>
            <p>${this.content?.description}</p>
          </section>
          <section id="cards">
            ${this.renderCarousel(this.content?.cards)}
            ${this.renderPagination(this.content?.cards.length ?? 0)}
          </section>
        </div>
        <web-kiosk-services-modal
          .card=${this.selectedCard}
          .appointment=${this.content?.appointment}
        ></web-kiosk-services-modal>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
      --web-kiosk-dimension-spacer-xxs: 8px;
      --web-kiosk-dimension-spacer-xs: 16px;
      --web-kiosk-dimension-spacer-m: 32px;
      --web-kiosk-dimension-spacer-l: 64px;
      --web-kiosk-services-availability-msg-color: #606565;
    }

    .page-body {
      height: 100%;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: url("assets/services/bg/staff.svg") left bottom -12% no-repeat,
        url("assets/services/bg/swirl.svg") no-repeat,
        var(--web-kiosk-bg, hotpink);
      background-size: calc(100vmin - 445px), cover;
    }

    .body-inner {
      padding: 48px 56px;
    }

    .body-content {
      display: flex;
    }

    #about-text {
      max-width: 33%;
      flex: 1 0 33%;
    }

    #about-text h1 {
      font-size: var(--web-kiosk-dimension-spacer-l, 64px);
      font-weight: 700;
      line-height: 72px;
      margin: var(--web-kiosk-dimension-spacer-m, 32px) 0;
    }

    a,
    #about-text :is(h1, h2) {
      color: var(--web-kiosk-text-color, hotpink);
    }

    #about-text h2 {
      margin: 42px 0 4px;
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
      font-weight: 700;
      line-height: 38px;
    }

    .card-texts p {
      margin: 0;
      color: var(--web-kiosk-services-availability-msg-color, hotpink);
      font-size: 24px;
      font-weight: 600;
      line-height: 34px;
    }

    #about-text p {
      margin: 0;
      color: var(--web-kiosk-text-color, hotpink);
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
      font-weight: 400;
      line-height: 52px;
    }

    #cards {
      width: 67%;
      display: flex;
      flex-wrap: wrap;
    }

    wh-carousel {
      width: 100%;
      margin: 18px 12px 0;
    }

    wh-carousel .carousel-slide {
      flex: 0 0 99%;
      min-height: 730px;
    }

    #pagination-container {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      margin-right: var(--web-kiosk-dimension-spacer-m, 32px);
    }

    .card-container {
      display: inline-block;
      width: calc(50% - var(--web-kiosk-dimension-spacer-m, 32px));
      margin: var(--web-kiosk-dimension-spacer-xs, 16px);
    }

    wh-card:hover {
      cursor: pointer;
    }

    .card-inner {
      display: flex;
      width: auto;
      height: 120px;
      padding: var(--web-kiosk-dimension-spacer-xs, 16px);
      align-items: center;
    }

    .card-inner img {
      height: fit-content;
    }

    .card-inner h3 {
      margin: 0 0 var(--web-kiosk-dimension-spacer-xxs, 8px);
      color: var(--web-kiosk-text-color, hotpink);
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
      font-weight: 700;
      line-height: 38px;
    }

    .card-icon {
      padding-right: var(--web-kiosk-dimension-spacer-m, 32px);
    }

    .arrow-icon {
      margin-left: auto;
    }

    wh-pagination {
      font-size: var(--web-kiosk-dimension-spacer-m, 32px);
    }
  `;
}
